"use client";

import { db } from "@utils/IndexedDb";
import { CatalogResponse } from "@utils/damac/api";
import { showOnlineExclusiveFeatureFlag } from "@utils/default";
import { useLocale } from "next-intl";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import React, { Dispatch, FC, ReactNode, Reducer, createContext, useCallback, useContext, useEffect, useReducer, useRef, useState } from "react";

import { allowedSearchParams, getQueryString, performSearch, queryStringToParams } from "@/components/ui/Form/Filters/FilterUtils";
import { locationsFromID } from "@/components/ui/Form/Filters/components/Autocomplete/AutocompleteUtils";
import { UserContext } from "@/context/UserContext";
import { filterSelectionTracking, resetFilterTracking } from "@/dataLayer/dataLayer";
import { CommercetoolsProject, Facet, PRODUCT_TYPE, ParamsAction, SearchParams, WORKER_ACTION } from "@/types";

export const pageSize = 12;
export const ANY_VALUE = "";
export const DEFAULT_SORT = "launchDate+desc";
export const DEFAULT_TYPE = PRODUCT_TYPE.PROPERTY_TYPE;

const updatePins = async (projectIDs: string[]) => {
  const pins = (await db.PINS.toArray()).map((pin) => {
    const categoryIDs = pin.categories.map((item) => item.id);
    const active = categoryIDs.filter((value) => projectIDs.includes(value as any)).length > 0;
    return { ...pin, active: active };
  });
  await db.PINS.bulkPut(pins);
};

interface SearchProps {
  filteredResults: CommercetoolsProject[];
  setFilteredResults: Dispatch<CommercetoolsProject[]>;
  filteredTotalResults: CommercetoolsProject[];
  total: number;
  counterTotal: number;
  dispatch: Dispatch<ParamsAction>;
  counterDispatch: Dispatch<ParamsAction>;
  params: SearchParams;
  counterParams: SearchParams;
  type: string;
  setType: Dispatch<string>;
  loadNextpage: () => void;
  selectedItem: string | undefined | null;
  setSelectedItem: Dispatch<string | undefined | null>;
  selectedPin: string | undefined | null;
  setSelectedPin: Dispatch<string | undefined | null>;
  isSearching: boolean;
  isLoading: boolean;
  isClearAll: boolean;
  setIsClearAll: Dispatch<boolean>;
  isRemoveFilter: string;
  setIsRemoveFilter: Dispatch<string>;
  setIsSearchPageTrachForFilterSelection?: any;
}
interface Props {
  children: ReactNode;
}

export const SearchContext = createContext<SearchProps>({
  filteredResults: [],
  setFilteredResults: () => {},
  filteredTotalResults: [],
  total: 0,
  counterTotal: 0,
  dispatch: () => {},
  counterDispatch: () => {},
  params: {} as SearchParams,
  counterParams: {} as SearchParams,
  type: DEFAULT_TYPE,
  setType: () => {},
  loadNextpage: () => {},
  selectedItem: null,
  setSelectedItem: () => {},
  selectedPin: null,
  setSelectedPin: () => {},
  isSearching: false,
  isLoading: false,
  isClearAll: false,
  setIsClearAll: () => false,
  isRemoveFilter: "",
  setIsRemoveFilter: () => "",
  setIsSearchPageTrachForFilterSelection: () => {},
});

async function handleFilterSelection(params: SearchParams) {
  if (params.category || params.bedrooms || params.price || params.propertyType) {
    let location = undefined;
    let propertyType = undefined;
    let bedrooms = undefined;
    let priceRange = undefined;

    if (params.category) {
      const locationArray = await locationsFromID(params.category as string[]);
      location = locationArray.length > 0 ? locationArray.join(", ") : undefined;
    }

    if (params.propertyType) {
      propertyType = Array.isArray(params.propertyType) ? params.propertyType.join(", ") : params.propertyType;
    }

    if (params.bedrooms) {
      bedrooms = Array.isArray(params.bedrooms) ? params.bedrooms.join(", ") : params.bedrooms;
    }

    if (params.price) {
      priceRange = Array.isArray(params.price) ? params.price.join(", ") : params.price;
    }

    filterSelectionTracking(location, propertyType, bedrooms, priceRange);
  }
}

function paramsReducer(currentState: SearchParams, action: ParamsAction): SearchParams {
  let newParams: SearchParams = {};
  if (action.action === "SEARCH") {
    newParams = { ...currentState };
  }
  if (action.action === "REMOVE") {
    newParams = { ...currentState };
    const removedKey = Object.keys(action.payload)[0] as keyof SearchParams;
    delete newParams[removedKey];

    switch (removedKey) {
      case "price":
        delete newParams.minimumUnitPrice;
        delete newParams.maximumUnitPrice;
        break;
    }
  }
  Object.entries(action.payload).forEach(([key, value]) => {
    if (allowedSearchParams.includes(key)) {
      if ([null, undefined, ""].includes(value as any)) {
        delete newParams[key as keyof SearchParams];
      } else {
        switch (key) {
          case "price":
            const [minPrice, maxPrice] = action.payload.price;
            newParams = { ...newParams, ...{ minimumUnitPrice: minPrice, maximumUnitPrice: maxPrice, price: [minPrice, maxPrice] } };
            break;
          case "area":
            const [minArea, maxArea] = action.payload.area;
            newParams = { ...newParams, ...{ minimumUnitArea: minArea, maximumUnitArea: maxArea, area: [minArea, maxArea] } };
            break;
          default:
            newParams = { ...newParams, ...{ [key]: value } };
            break;
        }
      }
    }
  });
  const sortedParams: Record<string, any> = {};
  Object.keys(newParams)
    .sort()
    .reduce((r, k) => ((r[k] = newParams[k as keyof SearchParams]), r), sortedParams);
  return sortedParams as SearchParams;
}

interface Props {
  children: ReactNode;
  catalogResponse?: CatalogResponse;
}

const SearchContextProvider: FC<Props> = ({ children, catalogResponse }) => {
  const query = useSearchParams();
  const pathname = usePathname();
  const router = useRouter();
  const initialized = useRef<boolean>();
  const locale = useLocale();
  const { currency, measurement } = useContext(UserContext);
  const [searchType, setSearchType] = useState(query.get("type") || DEFAULT_TYPE);
  const [filteredResults, setFilteredResults] = useState<CommercetoolsProject[]>(catalogResponse?.results ?? []);
  const [filteredTotalResults, setFilteredTotalResults] = useState<CommercetoolsProject[]>(catalogResponse?.results ?? []);
  const [totalResults, setTotalResults] = useState<number>(catalogResponse?.total ?? 0);
  const [counterTotalResults, setCounterTotalResults] = useState<number>(catalogResponse?.total ?? 0);
  const [params, dispatch] = useReducer<Reducer<SearchParams, ParamsAction>>(
    paramsReducer,
    query.size > 0 ? queryStringToParams(query) : ({} as SearchParams),
  );
  const [counterParams, counterDispatch] = useReducer<Reducer<SearchParams, ParamsAction>>(paramsReducer, params);

  // Track if params came from URL (navigation)
  useEffect(() => {
    if (query.size > 0) {
      isFromNavigation.current = true;
    }
  }, [query]);
  const [selectedItem, setSelectedItem] = useState<string | null | undefined>();
  const [selectedPin, setSelectedPin] = useState<string | null | undefined>();
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isClearAll, setIsClearAll] = useState<boolean>(false);
  const [isRemoveFilter, setIsRemoveFilter] = useState<string>("");
  const worker = useRef<Worker>();

  const prevCounterParamsRef = useRef<SearchParams | undefined>(undefined);
  const isInitialLoad = useRef<boolean>(true);
  const hasTrackedRef = useRef<boolean>(false);
  const isFromNavigation = useRef<boolean>(false);

  const isSearchPage = pathname?.includes("/search/") || false;
  const [isSearchPageTrachForFilterSelection, setIsSearchPageTrachForFilterSelection] = useState<boolean>(isSearchPage);

  /**
   * Initialize search
   */
  useEffect(() => {
    /**
     * Initialize webworker to load/update catalog
     * in different thread
     */
    if (!worker.current && !!currency && !!measurement) {
      worker.current = new Worker(new URL("../app/(workers)/catalog.ts", import.meta.url));
      worker.current.onmessage = (e: MessageEvent) => {
        switch (e?.data?.action) {
          case WORKER_ACTION.COMPLETED:
            initialized.current = true;
            console.log("data inizialiation completed");
            break;
        }
      };
      worker.current.onerror = (e: ErrorEvent) => {
        console.log(e);
      };
      worker.current.postMessage({
        action: WORKER_ACTION.INIT,
        payload: {
          currency,
          measurement,
        },
      });
      worker.current.postMessage({
        action: WORKER_ACTION.SETUP,
        payload: {
          url: window.location.href,
        },
      });
    } else if (worker.current && !!currency && !!measurement) {
      db.SETTINGS.orderBy("lastProjectUpdate")
        .reverse()
        .first()
        .then((lastUpdate) => {
          if ((lastUpdate?.currency !== currency || lastUpdate?.measurement !== measurement) && worker.current) {
            worker.current.postMessage({
              action: WORKER_ACTION.UPDATE,
              payload: {
                currency,
                measurement,
              },
            });
          }
        });
    }
  }, []);

  const loadNextpage = useCallback(() => {
    const paginate = async () => {
      if (totalResults > filteredResults.length) {
        setIsSearching(true);
        performSearch(params).then((results) => {
          setFilteredResults([...filteredResults, ...results.slice(filteredResults.length, filteredResults.length + pageSize)]);
          setIsSearching(false);
        });
      }
    };
    paginate();
  }, [params, filteredResults, totalResults, pageSize]);

  useEffect(() => {
    console.log("SearchContextProvider - params changed", isSearchPageTrachForFilterSelection, isInitialLoad.current);
    if (initialized.current || pathname?.includes("/search/")) {
      setIsLoading(true);
      setIsSearching(true);
      if (!isInitialLoad.current && !hasTrackedRef.current && !isFromNavigation.current && !isSearchPageTrachForFilterSelection) {
        handleFilterSelection(counterParams);
        hasTrackedRef.current = true;
        setTimeout(() => {
          hasTrackedRef.current = false;
        }, 100);
      } else {
        isInitialLoad.current = false;
        isFromNavigation.current = false;
        if (isInitialLoad.current) {
          resetFilterTracking();
        }
      }
      const cleanedParams: Record<string, any> = { ...counterParams };
      Object.keys(cleanedParams).forEach((key) => {
        if (key === "others" && !Array.isArray(cleanedParams[key])) {
          cleanedParams[key] = [cleanedParams[key]];
        }
      });
      if (cleanedParams.onlineExclusive === "false" || (cleanedParams.onlineExclusive === false && showOnlineExclusiveFeatureFlag)) {
        cleanedParams.onlineExclusive = "";
      }
      if (cleanedParams.webIE === "false" || cleanedParams.webIE === false) {
        cleanedParams.webIE = "";
      }
      if (cleanedParams.vTour === "false" || cleanedParams.vTour === false) {
        cleanedParams.vTour = "";
      }
      if (query.get("type") !== null) {
        cleanedParams.type = query.get("type");
      }
      performSearch(cleanedParams).then((results) => {
        setFilteredTotalResults(results);
        setCounterTotalResults(results.length);
        setIsSearching(false);
        setTimeout(() => {
          setIsLoading(false);
        }, 1000);
      });
    }
  }, [counterParams]);

  // useEffect(() => {
  //   if (!pathname?.includes("/search/")) {
  //     return;
  //   }
  //   // Rehydrate params from URL query string whenever query changes
  //   if (query.size > 0 && pathname?.includes("/search/")) {
  //     const searchParams = queryStringToParams(query);
  //     dispatch({ action: "SEARCH", payload: searchParams });
  //     counterDispatch({ action: "COUNT", payload: searchParams });
  //     performSearch(searchParams).then((results) => {
  //       const currentRawURL = new URL(window.location.href);
  //       const upCleaned = { ...searchParams };
  //       delete upCleaned.locale;
  //       const currentURL = `${currentRawURL.origin}/${locale}/search/?${getQueryString(upCleaned).toString()}`;
  //       setTotalResults(results.length);
  //       setCounterTotalResults(results.length);
  //       setFilteredTotalResults(results);
  //       setFilteredResults(results.slice(0, pageSize));
  //       // const projectIDs = [...new Set(filteredTotalResults.map((item) => item?.projectID))];
  //       const projectIDs = [...new Set((results || filteredTotalResults || [])?.map((item) => item?.projectID))];
  //       updatePins(projectIDs);
  //       router.push(currentURL);
  //       setIsSearching(false);
  //     });
  //   } else {
  //     const searchParams = queryStringToParams(query);
  //     dispatch({ action: "SEARCH", payload: searchParams });
  //     counterDispatch({ action: "COUNT", payload: searchParams });
  //   }
  // }, [query]);

  useEffect(() => {
    if (query.size > 0 && pathname.includes("/search/")) {
      const searchParams = queryStringToParams(query);
      dispatch({ action: "SEARCH", payload: searchParams });
      counterDispatch({ action: "COUNT", payload: searchParams });
    }
  }, [query]);

  useEffect(() => {
    if (initialized.current || pathname?.includes("/search/")) {
      setIsSearching(true);
      const cleanedParams: Record<string, any> = { ...params };
      setSelectedItem(undefined);

      Object.keys(cleanedParams).forEach((key) => {
        if (key === "others" && !Array.isArray(cleanedParams[key])) {
          cleanedParams[key] = [cleanedParams[key]];
        }
      });

      performSearch(cleanedParams).then((results) => {
        const currentRawURL = new URL(window.location.href);
        const upCleaned = { ...cleanedParams };
        delete upCleaned.locale;
        const currentURL = `${currentRawURL.origin}/${locale}/search/?${getQueryString(upCleaned).toString()}`;
        setTotalResults(results.length);
        setCounterTotalResults(results.length);
        setFilteredTotalResults(results);
        setFilteredResults(results.slice(0, pageSize));
        // const projectIDs = [...new Set(filteredTotalResults.map((item) => item?.projectID))];
        const projectIDs = [...new Set((results || filteredTotalResults || [])?.map((item) => item?.projectID))];
        updatePins(projectIDs);
        router.push(currentURL);
        setIsSearching(false);
      });
    }
  }, [params]);
  return (
    <SearchContext.Provider
      value={{
        total: totalResults,
        filteredResults,
        setFilteredResults,
        filteredTotalResults,
        dispatch,
        params,
        counterDispatch,
        counterTotal: counterTotalResults,
        counterParams,
        type: searchType,
        setType: setSearchType,
        loadNextpage,
        selectedItem,
        setSelectedItem,
        selectedPin,
        setSelectedPin,
        isSearching,
        isLoading,
        isClearAll,
        setIsClearAll,
        isRemoveFilter,
        setIsRemoveFilter,
        setIsSearchPageTrachForFilterSelection,
      }}
    >
      {children}
    </SearchContext.Provider>
  );
};
export default SearchContextProvider;
